import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"

export interface LightPathSockets extends MaterialNodeSockets {
    inputs: {}
    settings: {
        diffuseDepth: "float" // found in inputs: -; found in settings: float; inferred from data: float
        glossyDepth: "float" // found in inputs: -; found in settings: float; inferred from data: float
        isCameraRay: "float" // found in inputs: -; found in settings: float; inferred from data: float
        isDiffuseRay: "float" // found in inputs: -; found in settings: float,output; inferred from data: float,null
        isGlossyRay: "float" // found in inputs: -; found in settings: float,output; inferred from data: float,null
        isReflectionRay: "float" // found in inputs: -; found in settings: float; inferred from data: float
        isShadowRay: "float" // found in inputs: -; found in settings: float,output; inferred from data: float,null
        isSingularRay: "float" // found in inputs: -; found in settings: float; inferred from data: float
        isTransmissionRay: "float" // found in inputs: -; found in settings: float; inferred from data: float
        rayDepth: "float" // found in inputs: -; found in settings: float,output; inferred from data: float,null
        rayLength: "float" // found in inputs: -; found in settings: float; inferred from data: float
        transmissionDepth: "float" // found in inputs: -; found in settings: float; inferred from data: float
        transparentDepth: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    outputs: {
        isCameraRay: "float"
        isShadowRay: "float"
        isDiffuseRay: "float"
        isGlossyRay: "float"
        isSingularRay: "float"
        isReflectionRay: "float"
        isTransmissionRay: "float"
        rayLength: "float"
        rayDepth: "float"
        diffuseDepth: "float"
        glossyDepth: "float"
        transparentDepth: "float"
        transmissionDepth: "float"
    }
}

export const lightPathDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeLightPath,
    },
    nodeClass: "LightPath",
    ui: {
        label: "Light Path",
        color: "#4987af",
        items: [
            {name: "isCameraRay", valueType: "float", label: "Is Camera Ray", output: true},
            {name: "isShadowRay", valueType: "float", label: "Is Shadow Ray", output: true},
            {name: "isDiffuseRay", valueType: "float", label: "Is Diffuse Ray", output: true},
            {name: "isGlossyRay", valueType: "float", label: "Is Glossy Ray", output: true},
            {name: "isSingularRay", valueType: "float", label: "Is Singular Ray", output: true},
            {name: "isReflectionRay", valueType: "float", label: "Is Reflection Ray", output: true},
            {name: "isTransmissionRay", valueType: "float", label: "Is Transmission Ray", output: true},
            {name: "rayLength", valueType: "float", label: "Ray Length", output: true},
            {name: "rayDepth", valueType: "float", label: "Ray Depth", output: true},
            {name: "diffuseDepth", valueType: "float", label: "Diffuse Depth", output: true},
            {name: "glossyDepth", valueType: "float", label: "Glossy Depth", output: true},
            {name: "transparentDepth", valueType: "float", label: "Transparent Depth", output: true},
            {name: "transmissionDepth", valueType: "float", label: "Transmission Depth", output: true},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({
            diffuseDepth: materialFloatOutput,
            glossyDepth: materialFloatOutput,
            isCameraRay: materialFloatOutput,
            isDiffuseRay: materialFloatOutput,
            isGlossyRay: materialFloatOutput,
            isReflectionRay: materialFloatOutput,
            isShadowRay: materialFloatOutput,
            isSingularRay: materialFloatOutput,
            isTransmissionRay: materialFloatOutput,
            rayDepth: materialFloatOutput,
            rayLength: materialFloatOutput,
            transmissionDepth: materialFloatOutput,
            transparentDepth: materialFloatOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({}),
            settings: z.strictObject({
                diffuseDepth: z.optional(z.number()),
                glossyDepth: z.optional(z.number()),
                isCameraRay: z.optional(z.number()),
                isDiffuseRay: z.optional(z.number()),
                isGlossyRay: z.optional(z.number()),
                isReflectionRay: z.optional(z.number()),
                isShadowRay: z.optional(z.number()),
                isSingularRay: z.optional(z.number()),
                isTransmissionRay: z.optional(z.number()),
                rayDepth: z.optional(z.number()),
                rayLength: z.optional(z.number()),
                transmissionDepth: z.optional(z.number()),
                transparentDepth: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<LightPathSockets>
